import { zodResolver } from "@hookform/resolvers/zod"
import { useEffect, useRef, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import UnsavedChangesGuard from "~/v2/components/ui/unsaved-changes-guard"
import VaasCourseSection from "~/v2/components/vaas/vaas-course-section"
import VaasHeader from "~/v2/components/vaas/vaas-header"
import VaasHistoryDialog from "~/v2/components/vaas/vaas-history-dialog"
import VaasLearningCuesSection from "~/v2/components/vaas/vaas-learning-cues-section"
import VaasLessonMetadataSection from "~/v2/components/vaas/vaas-lesson-metadata-section"
import VaasLocationsSection from "~/v2/components/vaas/vaas-locations-section"
import VaasMediaSection from "~/v2/components/vaas/vaas-media-section"
import VaasVersionDialog from "~/v2/components/vaas/vaas-version-dialog"
import { buildEditVaasDefaults, editVaasSchema } from "~/v2/lib/vaas-schema"
import type {
  VaasCourse,
  VaasEditValues,
  VaasLesson,
  VaasThumbnail,
} from "~/v2/types/vaas"

type VaasEditorState = {
  course?: VaasCourse | null
  lesson?: VaasLesson | null
}

type VaasEditorProps = {
  actionError?: string
  createCombinedVersion: () => Promise<VaasEditorState> | void
  course: VaasCourse
  lesson?: VaasLesson | null
  publishLoading?: boolean
  publishVaas: (args: {
    dirtyFields: Record<string, unknown>
    values: VaasEditValues
  }) => Promise<VaasEditorState>
  saveError?: string
  saveLoading?: boolean
  saveVaas: (
    values: VaasEditValues,
    dirtyFields: Record<string, unknown>,
  ) => Promise<VaasEditorState>
  saveVaasCourseToKv: () => Promise<unknown>
  unpublishLoading?: boolean
  unpublishVaas: (args: {
    dirtyFields: Record<string, unknown>
    values: VaasEditValues
  }) => Promise<VaasEditorState>
  vaasCourseSaveLoading?: boolean
  versionLoading?: boolean
}

type LearningCuesHandle = {
  save?: () => Promise<void> | void
}

const LearningCuesSection = VaasLearningCuesSection as any

const VaasEditor = ({
  actionError,
  createCombinedVersion,
  course,
  lesson,
  publishLoading,
  publishVaas,
  saveError,
  saveLoading,
  saveVaas,
  saveVaasCourseToKv,
  unpublishLoading,
  unpublishVaas,
  vaasCourseSaveLoading,
  versionLoading,
}: VaasEditorProps) => {
  const [versionDialogOpen, setVersionDialogOpen] = useState(false)
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false)
  const [cuesDirty, setCuesDirty] = useState(false)
  const [discardSignal, setDiscardSignal] = useState(0)
  const learningCuesRef = useRef<LearningCuesHandle | null>(null)
  const methods = useForm<any>({
    defaultValues: buildEditVaasDefaults({ course, lesson }),
    resolver: zodResolver(editVaasSchema) as any,
  })

  const {
    formState: { dirtyFields, isDirty },
    register,
  } = methods
  const isPublished = course?.has_been_published === 1
  const hasUnsavedChanges = isDirty || cuesDirty

  useEffect(() => {
    methods.reset(buildEditVaasDefaults({ course, lesson }))
    setCuesDirty(false)
  }, [course, lesson, methods])

  const saveFormChanges = async (values: VaasEditValues) => {
    if (!isDirty) {
      return { course, lesson }
    }

    const result = await saveVaas(
      values,
      dirtyFields as Record<string, unknown>,
    )
    methods.reset(buildEditVaasDefaults(result))
    return result
  }

  const handleSave = methods.handleSubmit(async (values: VaasEditValues) => {
    await saveFormChanges(values)

    if (cuesDirty) {
      await learningCuesRef.current?.save?.()
      setCuesDirty(false)
    }
  })

  const handlePublish = methods.handleSubmit(async (values: VaasEditValues) => {
    if (cuesDirty) {
      await learningCuesRef.current?.save?.()
      setCuesDirty(false)
    }

    const result = await publishVaas({
      dirtyFields: dirtyFields as Record<string, unknown>,
      values,
    })
    methods.reset(buildEditVaasDefaults(result))
  })

  const handleUnpublish = methods.handleSubmit(
    async (values: VaasEditValues) => {
      if (cuesDirty) {
        await learningCuesRef.current?.save?.()
        setCuesDirty(false)
      }

      const result = await unpublishVaas({
        dirtyFields: dirtyFields as Record<string, unknown>,
        values,
      })
      methods.reset(buildEditVaasDefaults(result))
    },
  )

  const handleCreateVersion = async () => {
    await createCombinedVersion()
    setVersionDialogOpen(false)
  }

  const handleDiscardChanges = () => {
    methods.reset(buildEditVaasDefaults({ course, lesson }))
    setCuesDirty(false)
    setDiscardSignal((currentValue) => currentValue + 1)
  }

  const handleThumbnailUploaded = async (thumbnail: VaasThumbnail) => {
    const result = await saveVaas(
      { ...methods.getValues(), course_thumbnail: thumbnail },
      { course_thumbnail: true },
    )
    methods.reset(buildEditVaasDefaults(result))
  }

  if (!lesson) {
    return null
  }

  return (
    <FormProvider {...methods}>
      <>
        <form className="flex flex-col gap-6">
          <input {...register("disable_final_assessment")} type="hidden" />
          <VaasHeader
            actionError={actionError}
            course={course}
            isDirty={hasUnsavedChanges}
            onOpenHistoryDialog={() => setHistoryDialogOpen(true)}
            onOpenVersionDialog={() => setVersionDialogOpen(true)}
            onPublish={handlePublish}
            onSave={handleSave}
            onSaveVaasCourse={saveVaasCourseToKv}
            onUnpublish={handleUnpublish}
            publishLoading={publishLoading}
            saveError={saveError}
            saveLoading={saveLoading}
            unpublishLoading={unpublishLoading}
            vaasCourseSaveLoading={vaasCourseSaveLoading}
            versionLoading={versionLoading}
          />
          <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(360px,0.95fr)]">
            <div className="flex flex-col gap-6">
              <VaasCourseSection
                isPublished={isPublished}
                onThumbnailUploaded={handleThumbnailUploaded}
              />
              <VaasLessonMetadataSection />
              <VaasLocationsSection course={course} />
            </div>
            <div className="flex flex-col gap-6">
              <VaasMediaSection isPublished={isPublished} lesson={lesson} />
            </div>
          </div>
          <LearningCuesSection
            discardSignal={discardSignal}
            isPublished={isPublished}
            lesson={lesson}
            onDirtyChange={setCuesDirty}
            ref={learningCuesRef}
          />
        </form>
        <UnsavedChangesGuard
          hasUnsavedChanges={hasUnsavedChanges}
          isSaving={saveLoading}
          onDiscard={handleDiscardChanges}
          onSave={handleSave}
        />
      </>
      <VaasVersionDialog
        onConfirm={handleCreateVersion}
        onOpenChange={setVersionDialogOpen}
        open={versionDialogOpen}
        versionLoading={versionLoading}
      />
      <VaasHistoryDialog
        course={course}
        onOpenChange={setHistoryDialogOpen}
        open={historyDialogOpen}
      />
    </FormProvider>
  )
}

export default VaasEditor
